import type { CNSProjectNextStep, CNSProjectNextStepEntityType } from "@/models/cns";
import type { ArtifactTabId } from "@/lib/artifactTabUnlock";
import { focusToolFromNextStep } from "@/lib/workflowToolFocus";
import { buildWorkbenchConversationPath } from "@/lib/workbenchArtifactTabUrl";

const ENTITY_LIST_SEGMENT: Record<CNSProjectNextStepEntityType, string> = {
  hypothesis: "hypotheses",
  experiment: "experiments",
  learning: "learnings",
  insight: "insights",
  conversation: "conversations",
};

/**
 * Maps next-step deepLinkSuffix / entity type to a workbench artifact tab when useful.
 */
export function resolveWorkbenchArtifactTab(
  entityType: CNSProjectNextStepEntityType,
  deepLinkSuffix?: string | null,
): ArtifactTabId {
  switch (entityType) {
    case "learning":
      return "learning";
    case "insight":
      return "insights";
    case "experiment":
      return deepLinkSuffix ? "data-tracker" : "overview";
    default:
      return "overview";
  }
}

/**
 * Deep link for a next-step row: workbench when conversation known,
 * Conversations list when not, classical detail only as last resort for conversation entity without id mismatch.
 */
export function conversationIdFromNextStep(step: CNSProjectNextStep): string | null {
  return (
    step.conversationPublicId ??
    (step.entityType === "conversation" ? step.entityPublicId : null)
  );
}

/** Project home anchor for the conversation browse index. */
export const PROJECT_HOME_CONVERSATIONS_HASH = "#conversations";

/** Project home anchor for the HITL decision inbox. */
export const PROJECT_HOME_WAITING_HASH = "#waiting";

/**
 * Deep link to the project home conversation browse section.
 */
export function buildProjectHomeConversationsDeepLink(projectId: string): string {
  return `/dashboard/projects/${projectId}${PROJECT_HOME_CONVERSATIONS_HASH}`;
}

/**
 * Deep link to the project home HITL inbox section.
 */
export function buildProjectHomeWaitingDeepLink(projectId: string): string {
  return `/dashboard/projects/${projectId}${PROJECT_HOME_WAITING_HASH}`;
}

/**
 * Deep link for a next-step row: workbench when conversation known,
 * HITL inbox when not.
 */
export function buildNextStepDeepLink(step: CNSProjectNextStep): string {
  const conversationId = conversationIdFromNextStep(step);

  if (conversationId) {
    return buildWorkbenchConversationPath(
      step.projectPublicId,
      conversationId,
      resolveWorkbenchArtifactTab(step.entityType, step.deepLinkSuffix),
      { focusTool: focusToolFromNextStep(step) },
    );
  }

  return buildProjectHomeWaitingDeepLink(step.projectPublicId);
}

/**
 * Classical entity detail path (share / bookmark). Prefer {@link buildNextStepDeepLink} for CTAs.
 */
export function buildClassicalEntityPath(
  projectId: string,
  entityType: CNSProjectNextStepEntityType,
  entityPublicId: string,
  deepLinkSuffix?: string | null,
): string {
  if (entityType === "conversation") {
    return buildWorkbenchConversationPath(projectId, entityPublicId, "overview");
  }
  const segment = ENTITY_LIST_SEGMENT[entityType];
  const suffix = deepLinkSuffix ? `/${deepLinkSuffix}` : "";
  return `/dashboard/projects/${projectId}/${segment}/${entityPublicId}${suffix}`;
}

/**
 * Canonical browse path for project conversations on the unified project home.
 */
export function buildProjectConversationsPath(projectId: string): string {
  return buildProjectHomeConversationsDeepLink(projectId);
}

/**
 * Primary lifecycle CTA path: workbench when conversation known, conversation index otherwise.
 */
export function buildEntityLifecycleDeepLink(
  projectId: string,
  entityType: CNSProjectNextStepEntityType,
  entityPublicId: string,
  conversationPublicId?: string | null,
  deepLinkSuffix?: string | null,
): string {
  if (conversationPublicId) {
    return buildWorkbenchConversationPath(
      projectId,
      conversationPublicId,
      resolveWorkbenchArtifactTab(entityType, deepLinkSuffix),
    );
  }
  return buildProjectConversationsPath(projectId);
}

/**
 * Project home path without a section hash.
 */
export function buildProjectHomePath(projectId: string): string {
  return `/dashboard/projects/${projectId}`;
}
